import { useEffect, useMemo, useState } from "react";
import {
  Activity, AlertTriangle, Calendar, Coins, Database, DollarSign,
  Gauge, Globe, Loader2, PieChart as PieIcon, Plus, RefreshCw,
  Save, Sparkles, Trash2, TrendingUp,
} from "lucide-react";
import {
  AreaChart, Area, CartesianGrid, ResponsiveContainer, Tooltip as ReTooltip,
  XAxis, YAxis,
} from "recharts";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";

import { KpiCard, SectionCard } from "@/components/common/Cards";
import { api } from "@/lib/api";
import { errMsg } from "@/lib/api";
import { fmtNum, fmtRp, fmtRpShort } from "@/lib/format";

const TOOL_NAMES = {
  pajak: "Kalkulator Pajak",
  hosting: "COGS Hosting",
  isp: "COGS ISP",
  ekualizer: "Ekualizer Pajak",
  kartu_kredit: "Tagihan Kartu Kredit",
  meeting: "Meeting Eddy",
  scraper: "Scraping Kontak",
  admin_test: "Admin Test",
  unknown: "Lainnya",
};

const fmtDate = (iso) => {
  if (!iso) return "–";
  try {
    const d = new Date(iso);
    return d.toLocaleString("id-ID", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

export default function MonitorAiToolPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [snapOpen, setSnapOpen] = useState(false);
  const [cfgOpen, setCfgOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/ai-monitor/overview");
      setData(data);
    } catch (e) {
      toast.error(errMsg(e, "Gagal memuat data"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (!data) {
    return (
      <div className="max-w-[1280px] mx-auto space-y-4" data-testid="monitor-ai-loading">
        <Skeleton className="h-24 bg-[hsl(220_10%_11%)]" />
        <div className="grid md:grid-cols-4 gap-3">
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-28 bg-[hsl(220_10%_11%)]" />)}
        </div>
        <Skeleton className="h-80 bg-[hsl(220_10%_11%)]" />
      </div>
    );
  }

  const { snapshot, live, projection, costs, usage, config } = data;
  const totalTokensAll = usage.by_tool.reduce((s, r) => s + r.tokens, 0) || 1;

  return (
    <div className="max-w-[1280px] mx-auto space-y-6" data-testid="monitor-ai-page">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[3px] text-primary flex items-center gap-2">
            <Gauge className="h-3.5 w-3.5" /> Observability · Admin
          </div>
          <h1 className="font-display text-3xl sm:text-4xl mt-1">Monitor Token AI</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Pantau saldo Emergent Universal Key, pemakaian token per tool, dan prediksi biaya bulanan dalam Rupiah (AI + domain + server).
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => setHistoryOpen(true)} data-testid="monitor-history-btn">
            <Database className="h-4 w-4 mr-1.5" /> Riwayat snapshot
          </Button>
          <Button variant="outline" onClick={() => setCfgOpen(true)} data-testid="monitor-config-btn">
            <Sparkles className="h-4 w-4 mr-1.5" /> Pengaturan biaya
          </Button>
          <Button onClick={() => setSnapOpen(true)} data-testid="monitor-snapshot-btn">
            <Plus className="h-4 w-4 mr-1.5" /> Catat saldo Emergent
          </Button>
          <Button variant="ghost" size="icon" onClick={load} disabled={loading} data-testid="monitor-refresh">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      {!snapshot && (
        <div className="rounded-xl border border-[hsl(43_60%_30%)] bg-[hsl(43_30%_8%)] p-4 flex items-start gap-3" data-testid="monitor-no-snapshot">
          <AlertTriangle className="h-5 w-5 text-primary mt-0.5 shrink-0" />
          <div className="text-sm text-[hsl(45_30%_82%)]">
            <b className="text-foreground">Belum ada snapshot saldo.</b> Masuk ke emergent.sh → Account Settings → Credit Usage, lalu catat sisa kredit di sini. Konversi: <b>1 USD = 5 credits</b>. Tanpa snapshot, kami tidak bisa menghitung sisa saldo real-time atau prediksi tanggal habisnya.
          </div>
        </div>
      )}

      {/* KPI row */}
      <div className="grid md:grid-cols-4 gap-3">
        <KpiCard
          label="Saldo Emergent"
          value={live ? fmtRp(live.idr) : (snapshot ? fmtRp((snapshot.credits / 5) * costs.usd_to_idr) : "–")}
          sub={live ? `≈ $${live.usd.toFixed(2)} · ${fmtNum(live.credits)} credits` : (snapshot ? `Snapshot ${fmtDate(snapshot.captured_at)}` : "Belum ada data")}
          icon={Coins}
          testid="monitor-kpi-saldo"
          accent
        />
        <KpiCard
          label="Pemakaian AI bulan ini"
          value={fmtNum(usage.total_tokens_month)}
          sub={`${fmtNum(usage.total_requests_month)} request · ${fmtRpShort(costs.ai.mtd_idr)}`}
          icon={Activity}
          testid="monitor-kpi-usage"
        />
        <KpiCard
          label="Prediksi biaya bulan ini"
          value={fmtRpShort(costs.total.projected_idr)}
          sub={`MTD ${fmtRpShort(costs.total.mtd_idr)} · AI+domain+server`}
          icon={TrendingUp}
          testid="monitor-kpi-biaya"
          accent
        />
        <KpiCard
          label="Proyeksi habis saldo"
          value={projection ? `${projection.days_left} hari` : "–"}
          sub={projection ? `Habis ~${projection.depletion_date} · ${fmtRpShort(projection.burn_idr_per_day)}/hari` : "Perlu snapshot & aktivitas"}
          icon={AlertTriangle}
          testid="monitor-kpi-habis"
          tone={projection && projection.days_left < 14 ? "bad" : undefined}
        />
      </div>

      {/* Monthly cost breakdown */}
      <SectionCard
        title="Rincian biaya bulanan (Rupiah)"
        description={`Kurs USD→IDR: Rp ${fmtNum(costs.usd_to_idr)} · Tarif AI: $${costs.token_cost_per_1m_usd.toFixed(2)}/1 juta token (${costs.token_cost_source === "derived" ? "dihitung dari snapshot" : "estimasi pengaturan"})`}
        testid="monitor-costs-section"
      >
        <div className="grid md:grid-cols-3 gap-3">
          <CostTile
            icon={Sparkles}
            label="AI (Emergent)"
            mtd={costs.ai.mtd_idr}
            proj={costs.ai.projected_idr}
            detail={`${fmtNum(usage.total_tokens_month)} token · $${costs.ai.mtd_usd.toFixed(2)} MTD`}
            testid="monitor-cost-ai"
          />
          <CostTile
            icon={Globe}
            label="Domain"
            mtd={(costs.domain.monthly_idr * costs.day_of_month) / costs.days_in_month}
            proj={costs.domain.monthly_idr}
            detail={costs.domain.annual_idr > 0 ? `Rp ${fmtNum(costs.domain.annual_idr)} / tahun` : "Belum diatur"}
            testid="monitor-cost-domain"
          />
          <CostTile
            icon={Database}
            label="Server Emergent"
            mtd={(costs.server.monthly_idr * costs.day_of_month) / costs.days_in_month}
            proj={costs.server.monthly_idr}
            detail={costs.server.monthly_usd > 0 ? `$${costs.server.monthly_usd.toFixed(2)} / bulan` : "Belum diatur"}
            testid="monitor-cost-server"
          />
        </div>
        <div className="mt-4 rounded-xl border border-[color:var(--hairline)] bg-[hsl(43_30%_7%)] px-4 py-3 flex flex-wrap items-center justify-between gap-3" data-testid="monitor-cost-total">
          <div className="text-xs text-[hsl(45_20%_75%)]">
            Total prediksi akhir bulan ({costs.day_of_month}/{costs.days_in_month} hari berjalan)
          </div>
          <div className="flex items-baseline gap-3">
            <div className="text-xs text-muted-foreground">MTD <b className="text-foreground">{fmtRp(costs.total.mtd_idr)}</b></div>
            <div className="num text-2xl font-semibold text-primary">{fmtRp(costs.total.projected_idr)}</div>
          </div>
        </div>
      </SectionCard>

      {/* 30-day trend */}
      <SectionCard
        title="Tren pemakaian 30 hari terakhir"
        description="Token per hari (seluruh pengguna)"
        testid="monitor-trend-section"
      >
        <div className="h-72 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={usage.trend_30d} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="gold" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(43 80% 55%)" stopOpacity={0.6} />
                  <stop offset="100%" stopColor="hsl(43 80% 55%)" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="hsl(220 10% 15%)" vertical={false} />
              <XAxis
                dataKey="date"
                tickFormatter={(d) => d.slice(5)}
                stroke="hsl(45 15% 60%)"
                fontSize={11}
              />
              <YAxis stroke="hsl(45 15% 60%)" fontSize={11} tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v} />
              <ReTooltip
                contentStyle={{ background: "hsl(220 14% 6%)", border: "1px solid hsl(220 10% 18%)", borderRadius: 8, fontSize: 12 }}
                labelFormatter={(d) => d}
                formatter={(v) => [fmtNum(v) + " token", "Token"]}
              />
              <Area type="monotone" dataKey="tokens" stroke="hsl(43 85% 60%)" strokeWidth={2} fill="url(#gold)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </SectionCard>

      {/* Per-tool breakdown */}
      <SectionCard
        title="Breakdown per tool · bulan ini"
        description="Siapa yang paling banyak membakar token"
        testid="monitor-by-tool"
      >
        {usage.by_tool.length === 0 ? (
          <div className="py-10 text-center text-sm text-muted-foreground">Belum ada pemakaian AI bulan ini.</div>
        ) : (
          <div className="space-y-3">
            {usage.by_tool.map((r) => {
              const pct = Math.round((r.tokens / totalTokensAll) * 100);
              return (
                <div key={r.tool} className="flex items-center gap-4" data-testid={`monitor-tool-row-${r.tool}`}>
                  <div className="w-44 shrink-0">
                    <div className="text-sm font-medium">{TOOL_NAMES[r.tool] || r.tool}</div>
                    <div className="text-[11px] text-muted-foreground">{fmtNum(r.requests)} request</div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="h-2 rounded-full bg-[hsl(220_10%_14%)] overflow-hidden">
                      <div className="h-full bg-primary" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                  <div className="w-24 text-right num text-sm">{fmtNum(r.tokens)}</div>
                  <div className="w-28 text-right num text-sm text-primary">{fmtRp(r.idr)}</div>
                  <div className="w-10 text-right text-[11px] text-muted-foreground">{pct}%</div>
                </div>
              );
            })}
          </div>
        )}
      </SectionCard>

      <SnapshotDialog open={snapOpen} onOpenChange={setSnapOpen} onDone={load} usdToIdr={costs.usd_to_idr} />
      <ConfigDialog open={cfgOpen} onOpenChange={setCfgOpen} onDone={load} config={config} />
      <HistoryDialog open={historyOpen} onOpenChange={setHistoryOpen} onDone={load} usdToIdr={costs.usd_to_idr} />
    </div>
  );
}

function CostTile({ icon: Icon, label, mtd, proj, detail, testid }) {
  return (
    <div className="rounded-xl border border-[color:var(--hairline)] bg-[hsl(220_10%_7%)] p-4" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-[2px] text-muted-foreground">{label}</div>
        <Icon className="h-4 w-4 text-primary" />
      </div>
      <div className="mt-2 flex items-baseline gap-2">
        <div className="num text-xl font-semibold text-foreground">{fmtRp(proj)}</div>
        <div className="text-[11px] text-muted-foreground">prediksi</div>
      </div>
      <div className="mt-1 text-[11px] text-muted-foreground">
        MTD <b className="text-foreground num">{fmtRp(mtd)}</b> · {detail}
      </div>
    </div>
  );
}

function SnapshotDialog({ open, onOpenChange, onDone, usdToIdr }) {
  const [credits, setCredits] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (!open) { setCredits(""); setNote(""); } }, [open]);

  const save = async () => {
    const c = parseFloat(String(credits).replace(/[^0-9.]/g, ""));
    if (!Number.isFinite(c) || c < 0) return toast.error("Isi jumlah credits yang valid");
    setSaving(true);
    try {
      await api.post("/ai-monitor/snapshot", { credits: c, note });
      toast.success("Snapshot saldo tersimpan");
      onOpenChange(false);
      onDone();
    } catch (e) {
      toast.error(errMsg(e, "Gagal menyimpan snapshot"));
    } finally { setSaving(false); }
  };

  const usd = (parseFloat(credits) || 0) / 5;
  const idr = usd * (usdToIdr || 16000);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border sm:max-w-md" data-testid="monitor-snapshot-dialog">
        <DialogHeader>
          <DialogTitle>Catat saldo Emergent</DialogTitle>
          <DialogDescription>
            Buka <b>emergent.sh → Account Settings → Credit Usage</b>, lalu salin angka credits yang tersisa ke sini. Konversi: 1 USD = 5 credits.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Sisa credits</Label>
            <Input
              type="number"
              inputMode="decimal"
              value={credits}
              onChange={(e) => setCredits(e.target.value)}
              placeholder="mis. 450"
              className="h-10 bg-[hsl(220_12%_8%)] num"
              data-testid="monitor-snapshot-credits"
              autoFocus
            />
            {credits && (
              <div className="mt-2 text-[11px] text-muted-foreground">
                ≈ <b className="text-foreground">${usd.toFixed(2)}</b> · <b className="text-primary">{fmtRp(idr)}</b>
              </div>
            )}
          </div>
          <div>
            <Label className="text-xs">Catatan (opsional)</Label>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="mis. setelah top up $50"
              className="bg-[hsl(220_12%_8%)] min-h-[60px]"
              data-testid="monitor-snapshot-note"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Batal</Button>
          <Button onClick={save} disabled={saving} data-testid="monitor-snapshot-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Simpan snapshot
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ConfigDialog({ open, onOpenChange, onDone, config }) {
  const [form, setForm] = useState(() => ({ ...config }));
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setForm({ ...config }); }, [open, config]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/ai-monitor/config", {
        usd_to_idr: parseFloat(form.usd_to_idr) || 0,
        domain_annual_idr: parseFloat(form.domain_annual_idr) || 0,
        server_monthly_usd: parseFloat(form.server_monthly_usd) || 0,
        token_cost_per_1m_usd: parseFloat(form.token_cost_per_1m_usd) || 0,
      });
      toast.success("Pengaturan tersimpan");
      onOpenChange(false);
      onDone();
    } catch (e) {
      toast.error(errMsg(e, "Gagal menyimpan"));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border sm:max-w-lg" data-testid="monitor-config-dialog">
        <DialogHeader>
          <DialogTitle>Pengaturan biaya</DialogTitle>
          <DialogDescription>
            Nilai ini dipakai untuk menghitung biaya bulanan dalam Rupiah dan proyeksi sisa saldo.
          </DialogDescription>
        </DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3">
          <FieldNum label="Kurs USD → IDR" value={form.usd_to_idr} onChange={(v) => setForm({ ...form, usd_to_idr: v })} testid="monitor-cfg-usd" suffix="Rp/USD" />
          <FieldNum label="Biaya domain per tahun" value={form.domain_annual_idr} onChange={(v) => setForm({ ...form, domain_annual_idr: v })} testid="monitor-cfg-domain" suffix="Rp" />
          <FieldNum label="Biaya server Emergent" value={form.server_monthly_usd} onChange={(v) => setForm({ ...form, server_monthly_usd: v })} testid="monitor-cfg-server" suffix="USD / bulan" />
          <FieldNum label="Tarif AI per 1 juta token" value={form.token_cost_per_1m_usd} onChange={(v) => setForm({ ...form, token_cost_per_1m_usd: v })} testid="monitor-cfg-rate" suffix="USD (default jika belum ada 2 snapshot)" />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Batal</Button>
          <Button onClick={save} disabled={saving} data-testid="monitor-cfg-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Simpan
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FieldNum({ label, value, onChange, testid, suffix }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <Input
        type="number"
        inputMode="decimal"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="h-10 bg-[hsl(220_12%_8%)] num"
        data-testid={testid}
      />
      {suffix && <div className="mt-1 text-[10px] text-muted-foreground">{suffix}</div>}
    </div>
  );
}

function HistoryDialog({ open, onOpenChange, onDone, usdToIdr }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    if (!open) return;
    api.get("/ai-monitor/snapshots").then(({ data }) => setRows(data.items)).catch(() => setRows([]));
  }, [open]);

  const del = async (id) => {
    if (!confirm("Hapus snapshot ini?")) return;
    try {
      await api.delete(`/ai-monitor/snapshot/${id}`);
      setRows((r) => r.filter((x) => x.id !== id));
      onDone();
    } catch (e) {
      toast.error(errMsg(e, "Gagal menghapus"));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border sm:max-w-xl" data-testid="monitor-history-dialog">
        <DialogHeader>
          <DialogTitle>Riwayat snapshot saldo</DialogTitle>
          <DialogDescription>Snapshot terbaru dipakai sebagai acuan perhitungan live.</DialogDescription>
        </DialogHeader>
        <div className="max-h-[400px] overflow-y-auto">
          {rows === null ? (
            <Skeleton className="h-32" />
          ) : rows.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">Belum ada snapshot.</div>
          ) : (
            <table className="w-full text-sm" data-testid="monitor-history-table">
              <thead>
                <tr className="text-[11px] uppercase text-muted-foreground">
                  <th className="text-left py-2">Waktu</th>
                  <th className="text-right py-2">Credits</th>
                  <th className="text-right py-2">IDR</th>
                  <th className="text-left py-2 pl-3">Catatan</th>
                  <th className="w-10" />
                </tr>
              </thead>
              <tbody>
                {rows.map((r, idx) => (
                  <tr key={r.id} className="border-t border-border" data-testid={`monitor-history-row-${idx}`}>
                    <td className="py-2">{fmtDate(r.captured_at)}</td>
                    <td className="py-2 text-right num">{fmtNum(r.credits)}</td>
                    <td className="py-2 text-right num text-primary">{fmtRp((r.credits / 5) * (usdToIdr || 16000))}</td>
                    <td className="py-2 pl-3 text-xs text-muted-foreground">{r.note || "–"}</td>
                    <td className="py-2 text-right">
                      <Button size="icon" variant="ghost" onClick={() => del(r.id)} data-testid={`monitor-history-delete-${idx}`}>
                        <Trash2 className="h-3.5 w-3.5 text-red-400" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
